import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useLayoutEffect, useRef, useImperativeHandle, forwardRef, } from 'react';
const calculateBoundingBoxes = (refs) => {
    const boundingBoxes = {};
    Object.entries(refs).forEach(([key, node]) => {
        if (node) {
            boundingBoxes[key] = node.getBoundingClientRect();
        }
    });
    return boundingBoxes;
};
const Animate = forwardRef(({ children, refs }, ref) => {
    // store the "before" bounding boxes (set by .capture())
    const prevBoxesRef = useRef({});
    // expose API to parent so parent can snapshot before DOM mutation
    useImperativeHandle(ref, () => ({
        capture: () => {
            // snapshot the current DOM positions (before the reorder)
            prevBoxesRef.current = calculateBoundingBoxes(refs.current);
        },
    }), [refs]);
    useLayoutEffect(() => {
        const prevBoxes = prevBoxesRef.current;
        // nothing to animate if no snapshot taken
        if (!Object.keys(prevBoxes).length) {
            return;
        }
        // measure current (after DOM update)
        const newBoxes = calculateBoundingBoxes(refs.current);
        Object.keys(newBoxes).forEach((key) => {
            const node = refs.current[key];
            if (!node) {
                return;
            }
            const first = prevBoxes[key];
            const last = newBoxes[key];
            if (!first || !last) {
                return;
            }
            const deltaY = first.top - last.top; // vertical difference
            if (!deltaY) {
                return;
            }
            // invert: move element back to old position immediately
            node.style.transition = 'transform 0s';
            node.style.transform = `translateY(${deltaY}px)`;
            // force reflow so the browser registers the inline transform
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            node.getBoundingClientRect();
            // then on the next frame animate to natural position
            requestAnimationFrame(() => {
                // set the transition and remove the transform to animate
                node.style.transition = 'transform 500ms cubic-bezier(.2,.8,.2,1)';
                node.style.transform = '';
            });
            // cleanup after animation so the element isn't left with inline styles
            const onTransitionEnd = (e) => {
                if (e.propertyName !== 'transform') {
                    return;
                }
                node.style.transition = '';
                node.style.transform = '';
                node.removeEventListener('transitionend', onTransitionEnd);
            };
            node.addEventListener('transitionend', onTransitionEnd);
        });
        // clear snapshot to avoid accidental reuse
        prevBoxesRef.current = {};
    }, [children, refs]);
    return _jsx(_Fragment, { children: children });
});
export default Animate;
